import { Progress } from 'src/components/progress/Progress';
import { Column } from 'src/components/table';
import { IDaypart } from 'src/interfaces';
import { lang } from 'src/utils';

import { DeleteBundleAction } from '../calendarLocationGroupPage/modules/CalendarView/eventContentSidebar/EventContentSidebar.interface';
import { LocationSchedulePageTable } from '../scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable';
import { useLocationSchedulePage } from './hooks/ScheduleLocationPage.hook';

const columnsDefaultAssembly = [
  {
    id: 'startDate',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
  },
  {
    id: 'endDate',
    sortKey: 'valid_to',
    label: lang.endDate,
    sortIcon: true,
  },
  {
    id: 'assemblyName',
    label: lang.assemblyName,
    sortIcon: false,
  },
  {
    id: 'status',
    label: lang.status,
    sortIcon: false,
  },
  {
    id: 'assemblyScheduleDate',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
  },
];

const columnsScheduleComponent = [
  {
    id: 'name',
    sortKey: 'name',
    label: lang.scheduleComponent.scheduleNameField,
    sortIcon: true,
    width: 'calc(100% - 400px)',
  },
  {
    id: 'modified_at',
    sortKey: 'modified_at',
    label: lang.modifiedDate,
    sortIcon: true,
    width: '240px',
    align: 'left',
  },
  {
    id: 'override',
    label: 'Override',
    sortIcon: false,
    width: '200px',
    align: 'left',
  },
  {
    id: 'latest_override_date',
    sortKey: 'latest_override_date',
    label: 'Override date',
    sortIcon: true,
    width: '200px',
    align: 'left',
  },
];

const columnsScheduleComponentExpanded: Column[] = [
  {
    id: 'start_date',
    sortKey: 'start_date',
    label: lang.startDate,
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'end_date',
    sortKey: 'end_date',
    label: lang.endDate,
    sortIcon: true,
    width: '120px',
  },
  {
    id: 'name',
    sortKey: 'name',
    label: 'Component name',
    sortIcon: true,
    width: 'calc(100% - 240px)',
  },
  {
    id: 'override',
    label: 'Override',
    sortIcon: false,
    width: '200px',
    align: 'left',
  },
  {
    id: 'latest_override_date',
    sortKey: 'latest_override_date',
    label: 'Override date',
    sortIcon: true,
    width: '200px',
    align: 'left',
  },
];

// Individual location views always represent exactly one location.
// The shared schedule table uses this count to decide whether publish is needed
// after deleting schedules or schedule items.
const INDIVIDUAL_LOCATION_COUNT = 1;

export const ScheduleLocationPage = () => {
  const {
    individualLocationScheduleData,
    formattedSchedulesData,
    formMethods,
    onChangeLocationGroup,
    isLoadingScheduleDefaultAssemblies,
    isLoadingSchedules,
    refetchSchedules,
    locationGroup,
    isDefaultLocationGroup,
    page,
    totalPages,
    totalSchedulesPages,
    onPageChange,
    selectedDaypartId,
    selectedPositionId,
    daypartOptions,
    isLoadingDayparts,
    onDaypartChange,
    onPositionChange,
    selectedOption,
    onSelectedOptionChange,
    projectConfig,
    isDefaultAssembly,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
    setPage,
    sortSelected,
    setSortSelected,
    totalCountDefault,
    totalCountSchedule,
    rowsPerPage,
    handleRowsPerPageChange,
    excludeScheduleLocation,
    shouldShowListview,
  } = useLocationSchedulePage();

  if (
    Boolean(!formMethods.getValues().location) ||
    Boolean(
      isDefaultAssembly
        ? isLoadingScheduleDefaultAssemblies
        : isLoadingSchedules,
    )
  ) {
    return <Progress />;
  }

  const handleSortingClick = (columnId: string, direction: string) => {
    const selectedColumns = isDefaultAssembly
      ? columnsDefaultAssembly
      : columnsScheduleComponent;
    const column = selectedColumns.find((col) => col.id === columnId);

    if (!column?.sortKey) return;

    setSortKey(column.sortKey);
    setSortDirection(direction);
    setPage(1);
    setSortSelected(true);
  };

  return (
    <LocationSchedulePageTable
      columns={
        isDefaultAssembly ? columnsDefaultAssembly : columnsScheduleComponent
      }
      expandedColumns={
        isDefaultAssembly ? undefined : columnsScheduleComponentExpanded
      }
      tableData={
        isDefaultAssembly
          ? individualLocationScheduleData
          : formattedSchedulesData
      }
      locationGroupScheduleLoading={
        isDefaultAssembly
          ? isLoadingScheduleDefaultAssemblies
          : isLoadingSchedules
      }
      totalCount={isDefaultAssembly ? totalCountDefault : totalCountSchedule}
      onChangeLocationGroup={onChangeLocationGroup}
      locationData={formMethods.getValues().location}
      refetchSchedules={refetchSchedules}
      locationGroup={locationGroup}
      isLocationGroupSchedule={false}
      isDefaultLocationGroup={isDefaultLocationGroup}
      page={page}
      totalPage={isDefaultAssembly ? totalPages : totalSchedulesPages}
      onPageChange={onPageChange}
      selectedDaypartId={selectedDaypartId}
      selectedPositionId={selectedPositionId}
      daypartOptions={daypartOptions as IDaypart[]}
      isLoadingDayparts={isLoadingDayparts}
      onDaypartChange={onDaypartChange}
      onPositionChange={onPositionChange}
      selectedOption={selectedOption}
      onSelectedOptionChange={onSelectedOptionChange}
      projectConfig={projectConfig}
      sortDirection={{
        key: sortKey,
        direction: sortDirection,
        isSortModified: sortSelected,
      }}
      handleSortingClick={handleSortingClick}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={handleRowsPerPageChange}
      deleteBundleAction={excludeScheduleLocation as DeleteBundleAction}
      locationsCount={INDIVIDUAL_LOCATION_COUNT}
      shouldShowListview={shouldShowListview}
    />
  );
};
